import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Package, Truck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      
      if (user.role === 'vendor_partner' || user.role === 'core_loader') {
        navigate('/vendor-portal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  const initSystem = async () => {
    try {
      const response = await authAPI.init();
      toast.success('System initialized! Default credentials: admin@courier.com / admin123');
    } catch (error) {
      toast.error('Failed to initialize system');
    }
  };

  return (
    <div className="login-page" data-testid="login-page">
      {/* Hero Section */}
      <div 
        className="login-hero hidden lg:block"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758523670564-d1d6a734dc0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxjb3VyaWVyJTIwZGVsaXZlcnklMjBwYWNrYWdlfGVufDB8fHx8MTc2OTQyOTk2OHww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Truck className="w-10 h-10" />
            <span className="text-2xl font-bold font-['Chivo']">Velocity Courier</span>
          </div>
          <h1 className="text-4xl font-bold font-['Chivo'] mb-4 leading-tight">
            Streamline Your<br />Courier Operations
          </h1>
          <p className="text-lg text-slate-300 max-w-md">
            Manage ledgers, track consignments, and handle payments all in one place.
          </p>
        </div>
      </div>

      {/* Login Form */}
      <div className="login-form-container">
        <div className="max-w-md mx-auto w-full">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold font-['Chivo']">Velocity Courier</span>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold font-['Chivo'] text-slate-900 mb-2">
              Welcome back
            </h2>
            <p className="text-slate-600">
              Sign in to your account to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Username or Email</Label>
              <Input
                id="email"
                type="text"
                placeholder="admin"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-12 border-slate-200 focus:border-slate-900 focus:ring-slate-900/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-12 border-slate-200 focus:border-slate-900 focus:ring-slate-900/10"
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium btn-press"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-slate-200">
            <p className="text-sm text-slate-500 mb-4 text-center">First time setup?</p>
            <Button 
              variant="outline" 
              onClick={initSystem}
              data-testid="init-system-btn"
              className="w-full h-10 border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Initialize System
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
